// Port Wingo UI (React 19 + Tailwind 4) → Tailwind 3.4, preluat din portul ERP-ului (React 18). Vezi components/wingo/README.md.
// numbers in and out of text fields through Intl: formatting with a cached formatter, parsing what people type in
// their locale ("1.234,5" in ro-RO, "1,234.5" in en-US, and "12.5" typed on the wrong keyboard), step / precision
// math without float noise, currency affixes ("1.234,56 lei" vs "$1,234.56") and caret mapping for as-you-type
// grouping. no dependencies, no react

const formatters = new Map<string, Intl.NumberFormat>();

// a cached Intl.NumberFormat; creating one costs far more than formatting with it
export function getNumberFormatter(locale?: string, options: Intl.NumberFormatOptions = {}): Intl.NumberFormat {
  const key = `${locale ?? ""}|${JSON.stringify(options)}`;
  let formatter = formatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, options);
    formatters.set(key, formatter);
  }
  return formatter;
}

// "" for null, undefined and NaN, so an empty field stays empty
export function formatNumber(value: number | null | undefined, locale?: string, options?: Intl.NumberFormatOptions): string {
  if (value == null || Number.isNaN(value)) return "";
  return getNumberFormatter(locale, options).format(value);
}

export type NumberSeparators = {
  group: string;
  decimal: string;
  minus: string;
};

const separators = new Map<string, NumberSeparators>();

// the locale's characters: ro-RO { group: ".", decimal: ",", minus: "-" }, en-US { group: ",", decimal: "." }
export function getNumberSeparators(locale?: string): NumberSeparators {
  const key = locale ?? "";
  let result = separators.get(key);
  if (!result) {
    const parts = getNumberFormatter(locale, {
      useGrouping: true,
    }).formatToParts(-12345.6);
    result = {
      group: parts.find((p) => p.type === "group")?.value ?? ",",
      decimal: parts.find((p) => p.type === "decimal")?.value ?? ".",
      minus: parts.find((p) => p.type === "minusSign")?.value ?? "-",
    };
    separators.set(key, result);
  }
  return result;
}

const SPACES = /[\s\u00a0\u202f\u2009\u2019']/g;
const MINUS = /^[-\u2212\u2012\u2013]/;

// what a person typed, as a number, or null when it is empty or not a number. the locale's decimal separator wins;
// without it, a lone "." or "," followed by exactly three digits is a thousands separator ("1.234" = 1234 in ro-RO)
// and anything else is a decimal point ("12.5" = 12.5 even in ro-RO, where "." groups). currency symbols, units,
// spaces and "%" are ignored; the unicode minus and dashes count as a minus sign
export function parseNumber(text: string, locale?: string): number | null {
  const { group, decimal } = getNumberSeparators(locale);
  let source = text.trim().replace(SPACES, "");
  const negative = MINUS.test(source) || /\([\d.,]+\)/.test(source);
  source = source.replace(/[^\d.,]/g, "");
  if (!/\d/.test(source)) return null;

  let integer = source;
  let fraction = "";
  const lastDecimal = source.lastIndexOf(decimal);
  if (lastDecimal !== -1 && (decimal !== group || source.indexOf(decimal) === lastDecimal)) {
    integer = source.slice(0, lastDecimal);
    fraction = source.slice(lastDecimal + 1);
  } else {
    const other = decimal === "." ? "," : ".";
    const marks = Array.from(source.matchAll(/[.,]/g));
    const last = marks[marks.length - 1];
    if (marks.length === 1 && last) {
      const after = source.length - (last.index ?? 0) - 1;
      const isGroup = after === 3 && (last[0] === group || last[0] === other);
      if (!isGroup) {
        integer = source.slice(0, last.index);
        fraction = source.slice((last.index ?? 0) + 1);
      }
    }
  }
  integer = integer.replace(/[.,]/g, "");
  fraction = fraction.replace(/[.,]/g, "");
  const value = Number(`${negative ? "-" : ""}${integer || "0"}${fraction ? `.${fraction}` : ""}`);
  return Number.isFinite(value) ? value : null;
}

export function clamp(value: number, min = -Infinity, max = Infinity): number {
  return Math.min(Math.max(value, min), max);
}

// how many decimals a number (usually a step) carries: 0.05 -> 2, 1e-7 -> 7, 10 -> 0
export function decimalsOf(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const [mantissa, exponent] = String(value).toLowerCase().split("e");
  const decimals = (mantissa.split(".")[1] ?? "").length - Number(exponent ?? 0);
  return Math.max(0, decimals);
}

// rounds away float noise: roundTo(0.1 + 0.2, 2) === 0.3
export function roundTo(value: number, decimals: number): number {
  const factor = 10 ** Math.max(0, Math.min(decimals, 15));
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

// the nearest multiple of step counted from origin (usually min): snapToStep(23, 5) === 25, snapToStep(7, 5, 2) === 7
export function snapToStep(value: number, step: number, origin = 0): number {
  if (!(step > 0)) return value;
  const decimals = Math.max(decimalsOf(step), decimalsOf(origin));
  // round the step count too, or 0.35 / 0.1 = 3.4999... snaps down
  return roundTo(origin + Math.round(roundTo((value - origin) / step, 9)) * step, decimals);
}

export type CurrencyParts = {
  // "lei", "€", "$", "RON"
  symbol: string;
  // where the locale puts the symbol
  position: "prefix" | "suffix";
  // whether the locale puts a space between symbol and number
  spaced: boolean;
  // RON 2, JPY 0, BHD 3
  fractionDigits: number;
};

// how a locale writes a currency: getCurrencyParts("RON", "ro-RO") -> { symbol: "lei", position: "suffix", spaced: true,
// fractionDigits: 2 }. display defaults to narrowSymbol, which is what people write ("lei", "€", "$"); pass "code"
// for "RON" or "name" for "Romanian lei"
export function getCurrencyParts(
  currency: string,
  locale?: string,
  display: "symbol" | "narrowSymbol" | "code" | "name" = "narrowSymbol",
): CurrencyParts {
  const formatter = getNumberFormatter(locale, {
    style: "currency",
    currency,
    currencyDisplay: display,
  });
  const parts = formatter.formatToParts(1);
  const index = parts.findIndex((p) => p.type === "currency");
  const symbol = index === -1 ? currency : parts[index].value;
  const numberIndex = parts.findIndex((p) => p.type === "integer");
  const position = index !== -1 && index < numberIndex ? "prefix" : "suffix";
  const between = position === "prefix" ? parts[index + 1] : parts[index - 1];
  return {
    symbol,
    position,
    spaced: between?.type === "literal" && /\s/.test(between.value),
    fractionDigits: formatter.resolvedOptions().maximumFractionDigits ?? 2,
  };
}

export type EditableFormatOptions = {
  // decimals kept while typing (extra ones are cut, not rounded). default: as typed
  maxDecimals?: number;
  // group the integer part while typing ("1.234"). default true
  grouping?: boolean;
  allowNegative?: boolean;
};

// re-formats a field's raw text as the person types, keeping what they are in the middle of: a trailing decimal
// separator ("12,"), trailing zeros ("12,50") and a lone minus stay. returns the text to show
export function formatEditable(text: string, locale?: string, options: EditableFormatOptions = {}): string {
  const { group, decimal } = getNumberSeparators(locale);
  const { maxDecimals, grouping = true, allowNegative = true } = options;
  const trimmed = text.replace(SPACES, "");
  const negative = allowNegative && MINUS.test(trimmed);
  // the last decimal-looking mark the person typed becomes the decimal separator
  const body = trimmed.replace(/[^\d.,]/g, "");
  let decimalAt = body.lastIndexOf(decimal);
  if (decimalAt === -1 && decimal !== ".") {
    const dot = body.lastIndexOf(".");
    // a dot followed by up to two digits at the end reads as a decimal typed on an english keypad
    if (dot !== -1 && body.length - dot - 1 <= 2 && body.indexOf(".") === dot) decimalAt = dot;
  }
  const integerDigits = (decimalAt === -1 ? body : body.slice(0, decimalAt)).replace(/\D/g, "").replace(/^0+(?=\d)/, "");
  let fraction = decimalAt === -1 ? null : body.slice(decimalAt + 1).replace(/\D/g, "");
  if (fraction !== null && maxDecimals !== undefined) fraction = maxDecimals > 0 ? fraction.slice(0, maxDecimals) : null;
  const grouped = grouping ? integerDigits.replace(/\B(?=(\d{3})+(?!\d))/g, group) : integerDigits;
  const integer = grouped || (fraction !== null ? "0" : "");
  return `${negative ? "-" : ""}${integer}${fraction !== null ? `${decimal}${fraction}` : ""}`;
}

// caret helpers for fields that re-format while typing: count the digits (and the decimal separator) before the
// caret, re-format, then put the caret after the same count. separators the formatter inserts never move it
function countsAt(char: string, decimal: string) {
  return /\d/.test(char) || char === decimal;
}

export function digitsBeforeCaret(text: string, caret: number, locale?: string): number {
  const { decimal } = getNumberSeparators(locale);
  let count = 0;
  for (let i = 0; i < Math.min(caret, text.length); i++) if (countsAt(text[i], decimal)) count++;
  return count;
}

export function caretAfterDigits(text: string, count: number, locale?: string): number {
  if (count <= 0) return MINUS.test(text) ? 1 : 0;
  const { decimal } = getNumberSeparators(locale);
  let seen = 0;
  for (let i = 0; i < text.length; i++) {
    if (countsAt(text[i], decimal)) seen++;
    if (seen === count) return i + 1;
  }
  return text.length;
}
